<template>
  <div class="ruleset-viewer">
    <!-- Top-level mode: full evaluation data object -->
    <template v-if="data">
      <div v-for="ruleset in sortedEvaluatedRulesets" :key="ruleset.id" class="ruleset">
        <div class="ruleset-header">
          <div class="ruleset-title-row">
            <span class="ruleset-name">{{ ruleset.name }}</span>
            <span class="status-badge" :class="ruleset.criteriaMet ? 'status-badge--met' : 'status-badge--unmet'">
              {{ ruleset.criteriaMet ? "Met" : "Not Met" }}
            </span>
          </div>
          <div class="ruleset-subtype">{{ ruleset.infectionSubtype }}</div>
          <div class="ruleset-description">{{ ruleset.description }}</div>
        </div>
        <div class="ruleset-body">
          <ruleset-viewer :node="ruleset.ruleset"></ruleset-viewer>
        </div>
      </div>
    </template>

    <!-- Recursive node mode: CriteriaGroup or Criterion -->
    <template v-if="node">
      <!-- CriteriaGroup -->
      <div v-if="node.type === nodeTypes.CriteriaGroup" class="criteria-group" :class="node.criteriaMet ? 'criteria-group--met' : 'criteria-group--unmet'">
        <div class="group-header">
          <span class="operator-label" :class="node.logicalOperator === 'AND' ? 'operator-label--and' : 'operator-label--or'">
            {{ node.logicalOperator }}
          </span>
          <span v-if="node.logicalOperator === 'AND'" class="group-caption">All of the following must be satisfied:</span>
          <span v-else class="group-caption">At least one must be satisfied:</span>
          <span class="status-badge" :class="node.criteriaMet ? 'status-badge--met' : 'status-badge--unmet'">
            {{ node.criteriaMet ? "Met" : "Not Met" }}
          </span>
        </div>
        <ul class="children-list">
          <li v-for="(child, index) in node.children" :key="index" class="children-list__item">
            <ruleset-viewer :node="child"></ruleset-viewer>
          </li>
        </ul>
      </div>

      <!-- Criterion -->
      <div v-if="node.type === nodeTypes.Criterion" class="criterion" :class="node.criteriaMet ? 'criterion--met' : 'criterion--unmet'">
        <div class="criterion-header">
          <span class="criterion-type">{{ node.criteriaType }}</span>
          <span class="criterion-rule-desc">{{ formatOperator(node.rule.operator) }} {{ node.rule.value }}</span>
          <span class="status-badge" :class="node.criteriaMet ? 'status-badge--met' : 'status-badge--unmet'">
            {{ node.criteriaMet ? "Met" : "Not Met" }}
          </span>
        </div>
        <ul class="targets-list">
          <li
            v-for="target in node.rule.targets"
            :key="target.value"
            class="targets-list__item"
            :class="target.criterionMet ? 'targets-list__item--met' : 'targets-list__item--unmet'"
          >
            <span class="target-indicator"></span>
            <span class="target-id">{{ node.criteriaType === criteriaTypes.Symptom ? symptompLookup[target.value].displayText : organismLookup[target.value].name }}</span>
          </li>
        </ul>
      </div>
    </template>
  </div>
</template>

<script>
import util from "../../util";
import { CriteriaType, NodeType } from "./ruleset.constants";

export default {
  name: "RulesetViewer",
  props: {
    data: {
      type: Object,
      default: null
    },
    node: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      symptompLookup: {},
      organismLookup: {},
    };
  },
  computed: {
    criteriaTypes() {
      return CriteriaType;
    },
    nodeTypes() {
      return NodeType;
    },
    sortedEvaluatedRulesets() {
      if (!this.data?.evaluatedRulesets) return [];
      return [...this.data.evaluatedRulesets].sort((a, b) => b.criteriaMet - a.criteriaMet);
    }
  },
  methods: {
    formatOperator(operator) {
      const labels = {
        greaterThanOrEqualTo: "At least",
        greaterThan: "More than",
        lessThan: "Fewer than",
        lessThanOrEqualTo: "At most",
        equalTo: "Exactly"
      };
      return labels[operator] || operator;
    },
  },
  created() {
    this.symptompLookup = util.arrayToObj(this.$configStore.data.symptoms, "id");
    this.organismLookup = util.arrayToObj(this.$configStore.data.organisms, "id");
  },
};
</script>

<style scoped>
.ruleset-viewer {
  font-size: 0.9rem;
  color: #333;
}

/* Ruleset card */
.ruleset {
  border: 1px solid #d0d0d0;
  border-radius: 4px;
  margin-bottom: 1rem;
  overflow: hidden;
}

.ruleset-header {
  background: #f5f5f5;
  padding: 0.6rem 0.8rem;
  border-bottom: 1px solid #d0d0d0;
}

.ruleset-title-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.2rem;
}

.ruleset-name {
  font-weight: bold;
  font-size: 1rem;
}

.ruleset-subtype {
  font-size: 0.8rem;
  color: #666;
  margin-bottom: 0.1rem;
}

.ruleset-description {
  font-size: 0.8rem;
  color: #555;
  font-style: italic;
}

.ruleset-body {
  padding: 0.75rem;
  background: #fff;
}

/* Status badges */
.status-badge {
  display: inline-block;
  font-size: 0.75rem;
  font-weight: bold;
  padding: 0.1rem 0.45rem;
  border-radius: 3px;
  white-space: nowrap;
  margin-left: auto;
}

.status-badge--met {
  background: #d6f0c8;
  color: #3a7a1a;
  border: 1px solid #7cc156;
}

.status-badge--unmet {
  background: #fde8e8;
  color: #a02020;
  border: 1px solid #e08080;
}

/* Criteria group */
.criteria-group {
  border-left: 3px solid #ccc;
  padding: 0.5rem 0 0.5rem 0.75rem;
  margin-bottom: 0.4rem;
}

.criteria-group--met {
  border-left-color: #7cc156;
}

.criteria-group--unmet {
  border-left-color: #e08080;
}

.group-header {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin-bottom: 0.4rem;
  flex-wrap: wrap;
}

.operator-label {
  display: inline-block;
  font-size: 0.7rem;
  font-weight: bold;
  padding: 0.1rem 0.35rem;
  border-radius: 3px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.operator-label--and {
  background: #dce8f5;
  color: #1a4f8a;
  border: 1px solid #7aabdc;
}

.operator-label--or {
  background: #f5ecd8;
  color: #7a5000;
  border: 1px solid #dca83a;
}

.group-caption {
  font-size: 0.78rem;
  color: #555;
}

/* Children list */
.children-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.children-list__item {
  margin: 0;
  padding: 0;
}

/* Criterion */
.criterion {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 0.4rem 0.6rem;
  background: #fafafa;
}

.criterion--met {
  border-color: #7cc156;
  background: #f6fbf2;
}

.criterion--unmet {
  border-color: #e08080;
  background: #fff8f8;
}

.criterion-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.3rem;
}

.criterion-type {
  font-size: 0.75rem;
  font-weight: bold;
  color: #555;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.criterion-rule-desc {
  font-size: 0.8rem;
  color: #444;
}

/* Targets list */
.targets-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
}

.targets-list__item {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.75rem;
  padding: 0.1rem 0.4rem;
  border-radius: 3px;
  border: 1px solid #ddd;
  background: #f0f0f0;
  color: #555;
}

.targets-list__item--met {
  background: #d6f0c8;
  border-color: #7cc156;
  color: #3a7a1a;
}

.targets-list__item--unmet {
  background: #f0f0f0;
  border-color: #ccc;
  color: #666;
}

.target-indicator {
  display: inline-block;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: currentColor;
  flex-shrink: 0;
}

.target-id {
  white-space: nowrap;
}
</style>
